import { useRequest } from 'features/request/useRequest';
import type { ConstructionTypeData, ProjectData } from 'gql/graphql';
import { useAgGridDataSource } from 'hooks/useAgGridDataSource';
import { usePageFilterSaver } from 'hooks/usePageFilterSaver';

import type { AgGridReact } from 'ag-grid-react';
import type { RefObject } from 'react';
import { useCallback, useEffect, useMemo } from 'react';

import { getPriceListWhereVariables } from '../getWhereVariables';

import type { RowData } from './types';
import { priceListUerFlattened } from './uerQueries';

interface IUsePriceListUER {
    contractId: ProjectData['contractId'];
    filterValues: Record<string, string[]>;
    gridRef: RefObject<AgGridReact<RowData>>;
    constructionTypesData: ConstructionTypeData[];
}

export const usePriceListUER = ({ contractId, filterValues, gridRef, constructionTypesData }: IUsePriceListUER) => {
    const variables = useMemo(
        () => ({
            contractId,
            take: 50,
            where: getPriceListWhereVariables(filterValues),
        }),
        [contractId, filterValues],
    );

    const [priceListUerData, , , makeRequestPriceListUer] = useRequest({
        requestId: 'priceListUer',
        query: priceListUerFlattened,
        withEffect: false,
    });

    const { dataSource, getRowParams } = useAgGridDataSource({
        requestId: 'priceListUer',
        requestFn: makeRequestPriceListUer,
        requestVariables: variables,
    });

    useEffect(() => {
        if (priceListUerData?.uerPriceListUer?.items && getRowParams?.current) {
            getRowParams?.current?.successCallback(
                priceListUerData.uerPriceListUer.items,
                priceListUerData.uerPriceListUer.totalCount,
            );
        }
    }, [getRowParams, priceListUerData?.uerPriceListUer?.items, priceListUerData?.uerPriceListUer?.totalCount]);

    const refreshData = useCallback(() => {
        if (gridRef.current?.api) {
            gridRef.current.api.refreshInfiniteCache();
        }
    }, [gridRef]);

    usePageFilterSaver({
        withFakeSetter: !!constructionTypesData.length,
    });

    const totalCount = useMemo(
        () => priceListUerData?.uerPriceListUer?.totalCount ?? 0,
        [priceListUerData?.uerPriceListUer?.totalCount],
    );

    const fetchedCount = useMemo<number>(() => {
        if (!getRowParams?.current?.endRow || !totalCount) {
            return 0;
        }

        if (getRowParams?.current?.endRow > totalCount) {
            return totalCount;
        }

        return getRowParams.current?.endRow;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [totalCount, getRowParams?.current?.endRow]);

    return useMemo(
        () => ({
            datasource: dataSource,
            totalCount,
            fetchedCount,
            refreshData,
        }),
        [dataSource, totalCount, fetchedCount, refreshData],
    );
};
